import { UnreachableError } from 'base/helpers';
import { Button } from 'base/ui/button';
import {
  IconBell,
  IconComment,
  IconDesktop,
  IconMobile,
  IconTablet,
} from 'base/ui/icons';
import { Inline, Rows } from 'base/ui/layout';
import { ExpandToEdges, PanelLayout } from 'base/ui/panel';
import { TabList } from 'base/ui/tabs';
import { ToastQueue } from 'base/ui/toast';
import { TextSmall, TextTiny, TextTinyLight } from 'base/ui/typography';
import { CommentThread } from 'blocks/blocks_service';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { Avatars } from './avatars';
import { openNotificationSettings } from './notification_settings';
import styles from './sidebar_panel.css';
import {
  CommentContent,
  ThreadMenuButton,
  ThreadResolveButton,
  uniqueAvatars,
} from './ui';
import { CommentingUiStore, ThreadFilter } from './ui_store';

export const CommentingSidebarPanel = observer(function _CommentingSP({
  store,
  toastQueue,
}: {
  store: CommentingUiStore;
  toastQueue: ToastQueue;
}) {
  const navigation = React.useMemo(
    () => ({
      close: () => (store.addMode = false),
    }),
    [store],
  );
  const onTabChange = React.useCallback(
    (value: ThreadFilter) => (store.addModeFilter = value),
    [store],
  );
  const threads = store.comments.allThreads.filter(store.filterFn);
  const emptyStateRef = React.useRef<HTMLDivElement>(null);
  const getRef = useRefMap<HTMLDivElement>();
  const showEmptyState = threads.length === 0;

  return (
    <PanelLayout
      title="Comments"
      navigation={navigation}
      hideHeaderDivider={true}
      disableWrapChildren={showEmptyState}
      extraButton={
        store.loggedIn ? (
          <Button
            variant="subtle"
            size="small"
            icon={IconBell}
            tooltip="Notification settings"
            onClick={openNotificationSettings}
          />
        ) : undefined
      }
      header={
        <ExpandToEdges>
          <TabList
            options={FILTER_OPTIONS}
            selectedValue={store.addModeFilter}
            onChange={onTabChange}
          />
        </ExpandToEdges>
      }
    >
      {showEmptyState ? (
        <EmptyState
          nodeRef={emptyStateRef}
          title={
            store.addModeFilter === 'active'
              ? 'No comments'
              : 'No resolved comments'
          }
          text={
            store.addModeFilter === 'active'
              ? 'Click anywhere on the canvas to leave a comment.'
              : 'You can resolve comments by clicking the tick icon.'
          }
        />
      ) : (
        <Rows spacing={8}>
          <TransitionGroup component={null}>
            {threads.map((thread) => (
              <CSSTransition
                key={thread.id}
                nodeRef={getRef(thread.id)}
                classNames={styles}
                timeout={150}
              >
                <ThreadCard
                  nodeRef={getRef(thread.id)}
                  thread={thread}
                  store={store}
                  toastQueue={toastQueue}
                />
              </CSSTransition>
            ))}
          </TransitionGroup>
        </Rows>
      )}
    </PanelLayout>
  );
});

function useRefMap<T>(): (key: number) => React.MutableRefObject<T | null> {
  const map = React.useRef(new Map<number, React.MutableRefObject<T | null>>());
  return React.useCallback((key: number) => {
    if (!map.current.has(key)) {
      map.current.set(key, React.createRef());
    }
    return map.current.get(key)!;
  }, []);
}

const ThreadCard = observer(function _ThreadCard({
  thread,
  store,
  toastQueue,
  nodeRef,
}: {
  thread: CommentThread;
  store: CommentingUiStore;
  toastQueue: ToastQueue;
  nodeRef?: React.Ref<HTMLDivElement>;
}) {
  const ref = React.useRef<HTMLDivElement>(null);

  const comments = store.comments.getCommentsInThread(thread.id);
  const firstComment = comments[0];
  const avatars = uniqueAvatars(comments);

  const isSelected = store.selectedThreadId === thread.id;
  const isCanvasHovering = store.canvasHoveringThreadId === thread.id;
  const isRead = store.comments.isThreadRead(thread.id);

  const onClick = React.useCallback(() => {
    if (store.selectedThreadId === thread.id) {
      store.selectedThreadId = undefined;
    } else {
      store.goToCommentThread(thread.id);
    }
  }, [store, thread.id]);
  const onMouseEnter = React.useCallback(() => {
    store.sidebarHoveringThreadId = thread.id;
  }, [store, thread.id]);
  const onMouseLeave = React.useCallback(() => {
    if (store.sidebarHoveringThreadId === thread.id) {
      store.sidebarHoveringThreadId = undefined;
    }
  }, [store, thread.id]);
  React.useEffect(() => onMouseLeave, [onMouseLeave]);

  React.useEffect(() => {
    const el = ref.current;
    if (!el || !isSelected) return;
    // If the comment was selected on the canvas, it might not be in view
    // in the sidebar.
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [isSelected]);

  React.useImperativeHandle(nodeRef, () => ref.current!);

  return (
    <div
      className={classNames(
        styles.threadCard,
        isSelected && styles.isSelected,
        isCanvasHovering && styles.isCanvasHovering,
        thread.resolved && styles.isResolved,
      )}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={ref}
    >
      <Rows spacing={8}>
        <div className={styles.threadCardTop}>
          <div
            className={classNames(
              styles.threadCardAvatars,
              thread.resolved && styles.isResolved,
            )}
          >
            <Avatars avatars={avatars} max={6} isUnread={!isRead} />
          </div>
          <div
            className={styles.threadCardMenu}
            // Opening the menu should not select the thread.
            onClick={stopPropagation}
          >
            <ThreadMenuButton
              thread={thread}
              store={store}
              toastQueue={toastQueue}
            />
          </div>
          <div
            className={classNames(
              styles.threadCardResolve,
              !thread.resolved && styles.isUnresolved,
            )}
            // Resolving the thread should not select the thread.
            onClick={stopPropagation}
          >
            <ThreadResolveButton
              thread={thread}
              store={store}
              toastQueue={toastQueue}
            />
          </div>
        </div>
        <CommentContent
          comment={firstComment}
          contentLineClamp={3}
          faded={isRead}
        />
        <AnchoringDescription thread={thread} store={store} />
        {comments.length > 1 && (
          <TextTiny color="grey">
            {comments.length - 1} {comments.length === 2 ? 'reply' : 'replies'}
          </TextTiny>
        )}
      </Rows>
    </div>
  );
});

function stopPropagation(e: React.MouseEvent) {
  e.stopPropagation();
}

const AnchoringDescription = observer(function _AnchoringDescription({
  thread,
  store,
}: {
  thread: CommentThread;
  store: CommentingUiStore;
}) {
  const selection = store.comments.getThreadSelectionAndAnchoring(thread.id);
  if (selection == null) {
    return null;
  }
  let viewportName: string;
  let Icon;
  switch (selection.anchoring.viewport) {
    case 'sitemap':
      viewportName = 'Sitemap';
      break;
    case 'wireframe-desktop':
      viewportName = 'Wireframe';
      Icon = IconDesktop;
      break;
    case 'wireframe-tablet':
      viewportName = 'Wireframe';
      Icon = IconTablet;
      break;
    case 'wireframe-mobile':
      viewportName = 'Wireframe';
      Icon = IconMobile;
      break;
    case 'styleguide':
      viewportName = 'Styleguide';
      break;
    default:
      throw new UnreachableError(selection.anchoring.viewport);
  }
  const pageNamePart = selection.sel.page
    ? ` • ${selection.sel.page.name}`
    : '';
  return (
    <Inline spacing={4} yalign="center">
      <TextTinyLight color="grey">
        {viewportName}
        {pageNamePart}
      </TextTinyLight>
      {Icon && (
        <div className={styles.anchoringIcon}>
          <Icon />
        </div>
      )}
    </Inline>
  );
});

const FILTER_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'resolved', label: 'Resolved' },
] as const;

function EmptyState({
  title,
  text,
  nodeRef,
}: {
  title: string;
  text: string;
  nodeRef: React.Ref<HTMLDivElement>;
}) {
  return (
    <div className={styles.emptyState} ref={nodeRef}>
      <Rows spacing={16} xalign="center">
        <div className={styles.emptyStateIcon}>
          <IconComment />
        </div>
        <Rows spacing={4} xalign="center">
          <TextSmall align="center" color="grey">
            {title}
          </TextSmall>
          <TextTinyLight align="center" color="grey">
            {text}
          </TextTinyLight>
        </Rows>
      </Rows>
    </div>
  );
}
